import { IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip, Checkbox, Box, ListItem, List } from '@mui/material';
import { AccessAlarm, CheckCircleOutline, ErrorOutline, BlockRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import './RequestProposalTable.css';
import InfoDialog from '../../../../components/InfoDialog/InfoDialog';
import EmptyMessage from '../../../../components/Alerts/EmptyMessage';
import { RootState } from '../../../../shared/redux/store';
import Activity from '../../../../models/Activity';
import CommentIcon from '@mui/icons-material/Comment';
import SnippetFolderIcon from '@mui/icons-material/SnippetFolder';

const RequestProposalTable = () => {
  const { t } = useTranslation();
  const request = useSelector((state: RootState) => state?.requestSearch?.searchRequest);
  const equivalencies = request?.equivalencies || [];
  const [openRejectedDialog, setOpenRejectedDialog] = useState(false);
  const [minContentDialog, setMinContentDialog] = useState(false);
  const [rejectedComment, setRejectedComment] = useState<string | null>(null);
  const [minContent, setMinContent] = useState<JSX.Element | null>(null);

  const getsimulationActivityIcon = (state: string) => {
    switch (state) {
      case 'APPROVED':
        return <Tooltip placement='top' arrow title={<>{t('approved')}</>}>
          <div style={{ cursor: 'pointer' }} data-testid={'rowIcon'}>
            <CheckCircleOutline color='secondary' />
          </div>
        </Tooltip>;
      case 'PENDING':
        return <Tooltip placement='top' arrow title={<>{t('pending')}</>}>
          <div style={{ cursor: 'pointer' }} data-testid={'rowIcon'}>
            <AccessAlarm color='info' />
          </div>
        </Tooltip>
      case 'REJECTED':
        return <Tooltip placement='top' arrow title={<>{t('proposalActivityMesage')}</>}>
          <div style={{ cursor: 'pointer' }} data-testid={'rowIcon'}>
            <BlockRounded color='error' />
          </div>
        </Tooltip>
      default:
        return <Tooltip placement='top' arrow title={<>{t('rejected')}</>}>
          <div style={{ cursor: 'pointer' }} data-testid={'rowIcon'}>
            <ErrorOutline color='warning' />
          </div>
        </Tooltip>;
    }
  };
  const proposalsDisplay = equivalencies?.proposals ? equivalencies.proposals.map((proposal: { id: string; sourceActivity: string; targetActivity: string; state: string; }) => {
    return {
      id: proposal.id,
      source_activities: [proposal.sourceActivity],
      target_activities: [proposal.targetActivity],
      state: proposal.state,
      proposal
    }
  }) : [];

  const showRejectedComment = (comment: string) => {
    setRejectedComment(comment)
    setOpenRejectedDialog(true)
  }

  const showMinContent = (content: any) => {
    const generatedContent = content ? (
      <Box>
        <List sx={{ overflowY: 'auto', maxHeight: '400px' }} disablePadding>
          {content.minContentSelected.map((item: any) => (
            <ListItem key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', padding: '10px', alignItems: 'flex-start' }} >
              <Typography variant='body1'>{item.description}</Typography><Checkbox sx={{ padding: 0, marginLeft: '10px' }} checked={item.status} disabled data-testid={`checkbox-min-content-${item.id}`}/>
            </ListItem>
          )
          )}
        </List>
      </Box>
    ) : (
      <label>{t('minContentDescripcion')}</label>
    );
    setMinContentDialog(true)
    setMinContent(generatedContent);
  }

  const actionColumn = (proposal: { state: string; source_activities?: any; target_activities?: any; proposal: { rejectionReason: string, proposalMinContentSelected: string[] };}, tableRowIndex: number) => <TableCell className="EQXXI-RequestRequest-Name EQXXI-Request-Border EQXXI-RequestRequest-Actions" align='left' sx={{ backgroundColor: tableRowIndex % 2 === 0 ? '#efefef !important' : '#FFF !important' }} >
    <>
      {proposal.state === 'REJECTED' &&
        <Tooltip title={t('rejectedShowComment')}>
          <IconButton
            id='rejectComment'
            data-testid="rejected-comment-btn"
            className="EQXXI-RequestSimulateRow-Button withoutBorder small-icon"
            onClick={() => showRejectedComment(proposal.proposal.rejectionReason)}
          >
            <CommentIcon/>
          </IconButton>
        </Tooltip>
      }
      <Tooltip title={t('minContentShowComment')}>
        <IconButton
          id='minContentShowComment'
          data-testid="min-content-btn"
          className="EQXXI-RequestSimulateRow-Button withoutBorder"
          onClick={() => showMinContent(proposal.proposal.proposalMinContentSelected)}
        >
          <SnippetFolderIcon/>
        </IconButton>
      </Tooltip>

    </>
  </TableCell >

  return (
    <>
      <Typography
        variant="h6"
        color="primary"
        mb={4}
        mt={6}
      >
        {t('proposalTableTitle')}
      </Typography>
      <Paper variant="outlined" sx={{ width: '100%' }}>
        <TableContainer sx={{ flex: '1' }}>
          <Table stickyHeader className="EQXXI-RequestRequest-ActivitiesTable" size="small" aria-label="customized table">
            <TableHead>
              <TableRow>
                <TableCell className='EQXXI-RequestRequest-Border' align="center">
                  <Typography><>
                    {t('state')}</> </Typography>
                </TableCell>
                <TableCell className="EQXXI-RequestRequest-Name EQXXI-RequestRequest-Border" align="left">
                  <Typography>
                    <>
                      {t('source')}
                    </>
                  </Typography>
                </TableCell>
                <TableCell className="EQXXI-RequestRequest-Name EQXXI-RequestRequest-Border" align="left">
                  <Typography><>
                    {t('target')}</> </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '160px' }}>
                  <Typography><>
                    {t('actions')}</> </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className="REQXXI-EquivalencyRule-TableBody" data-testid="request-proposal-table-body" style={{ overflow: 'auto' }}>
              {proposalsDisplay.length > 0 && proposalsDisplay.map((row: { source_activities?: any; target_activities?: any; state: string; proposal: { rejectionReason:string, proposalMinContentSelected: string[] };}, tableRowIndex: number) => {
                const maxRows = Math.max(row.source_activities.length, row.target_activities.length);
                const renderActivityName = (activity: Activity) => {
                  if (!activity) return null;
                  return (
                    <div>
                      <p>{activity.name}</p>
                    </div>
                  );
                };
                return Array.from({ length: maxRows }).map((_, rowIndex) => {
                  return (
                    <TableRow key={rowIndex + row.source_activities[rowIndex]?.id + row.target_activities[rowIndex]?.id} sx={{ position: 'relative' }}>
                      <TableCell className='EQXXI-RequestRequest-Border' align="center" scope="row" sx={{ backgroundColor: tableRowIndex % 2 === 0 ? '#efefef !important' : '#FFF !important' }}>
                        {row.target_activities[rowIndex] || row.target_activities.length === 0 ? (
                          <div>{getsimulationActivityIcon(row.state)}</div>
                        ) : (
                          <div style={{ height: '40px' }}></div>
                        )}
                      </TableCell>
                      <TableCell className="EQXXI-RequestRequest-Name EQXXI-RequestRequest-Border" sx={{ backgroundColor: tableRowIndex % 2 === 0 ? '#efefef !important' : '#FFF !important' }}>
                        <Typography>{rowIndex < row.source_activities.length ? renderActivityName(row.source_activities[rowIndex]) : null}</Typography>
                      </TableCell>
                      <TableCell className="EQXXI-RequestRequest-Name EQXXI-RequestRequest-Border" sx={{ backgroundColor: tableRowIndex % 2 === 0 ? '#efefef !important' : '#FFF !important' }}>
                        {row.target_activities[rowIndex] ? (
                          <Typography>{renderActivityName(row.target_activities[rowIndex])}</Typography>
                        ) : (
                          <div style={{ height: '40px' }}></div>
                        )}
                      </TableCell>
                      {actionColumn(row, tableRowIndex)}
                    </TableRow>
                  );
                });
              })}
              {proposalsDisplay.length === 0 && <EmptyMessage />}
            </TableBody>
          </Table>
        </TableContainer >
      </Paper>
      <InfoDialog
        open={openRejectedDialog}
        title={t('rejectedCommentDialogTitle')}
        message={rejectedComment}
        onClose={() => {
          setOpenRejectedDialog(false)
          setRejectedComment(null)
        }}
      />
      <InfoDialog
        open={minContentDialog}
        title={t('minContentDialogTitle')}
        content={minContent}
        onClose={() => {
          setMinContentDialog(false)
          setMinContent(null)
        }}
      />
    </>
  );
};

export default RequestProposalTable;
